
'use client';

import { useEffect, useState, FormEvent } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { getSupabaseBrowser } from '@/lib/supabase-browser';
import VerifyOTPComponent from '@/components/auth/VerifyOTP';
import VerifyCustomCode from '@/components/auth/VerifyCustomCode';
import { normalizeBrazilWhatsapp } from '@/lib/whatsapp/phone-utils';

type Mode = 'signin' | 'signup' | 'verify-otp' | 'verify-custom' | 'forgot-password';

export default function LoginClient() {
  const router = useRouter();
  const params = useSearchParams();
  const next = params.get('next') || params.get('redirect') || '/';
  const message = params.get('message');

  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [normalizedWhatsappState, setNormalizedWhatsappState] = useState<string | null>(null);
  const [whatsappError, setWhatsappError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(message);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Flag para usar verificação dual-channel (WhatsApp + Email)
  const USE_DUAL_CHANNEL_VERIFICATION = true;

  // Função para decidir redirect baseado na role
  async function getRedirectDestination() {
    // 1) Se veio redirect explícito e é seguro, respeita
    if (next && next.startsWith('/') && !next.startsWith('//')) {
      return next;
    }

    // 2) Senão, decide por role usando priorização (mesma lógica do RBAC)
    const supabase = getSupabaseBrowser();
    const { data } = await supabase.auth.getUser();
    const userId = data?.user?.id;

    if (userId) {
      // Buscar TODAS as memberships e priorizar pilot
      const { data: allMemberships } = await supabase
        .from('org_members')
        .select('role, org_id')
        .eq('profile_id', userId);

      if (allMemberships && allMemberships.length > 0) {
        // Priorizar pilot > owner > admin (mesma lógica do settings.ts)
        const pilotMembership = allMemberships.find(m => m.role === 'pilot');
        if (pilotMembership) {
          return '/pilot/dashboard';
        }
        
        // Se não é pilot, vai para dashboard owner/admin
        const ownerAdminMembership = allMemberships.find(m => m.role === 'owner' || m.role === 'admin');
        if (ownerAdminMembership) {
          return '/dashboard';
        }
      }
    }

    // fallback
    return '/dashboard';
  }

  // Se já estiver logado, manda pra rota baseada na role
  useEffect(() => {
    (async () => {
      const supabase = getSupabaseBrowser();
      const { data } = await supabase.auth.getUser();
      if (data.user) {
        const destination = await getRedirectDestination();
        router.replace(destination as any);
      }
    })();
     
  }, []);

  async function syncHttpOnlyCookies() {
    const supabase = getSupabaseBrowser();
    const { data } = await supabase.auth.getSession();
    if (data?.session) {
      await fetch('/api/auth/set', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        }),
      });
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setSuccessMessage(null);
    setWhatsappError(null);
    setLoading(true);
    try {
      const supabase = getSupabaseBrowser();
      const cleanEmail = email.trim().toLowerCase();

      if (mode === 'signin') {
        const { error } = await supabase.auth.signInWithPassword({
          email: cleanEmail,
          password,
        });
        if (error) {
          console.error('[Login] Supabase auth error:', error);
          // Melhorar mensagem de erro baseada no tipo
          if (error.message.includes('Invalid login credentials')) {
            throw new Error('Email ou senha incorretos. Verifique se você confirmou sua conta via WhatsApp após o cadastro.');
          }
          throw error;
        }
        
        // Sincronizar cookies da sessão
        console.log('[AUTH_DEBUG][LoginClient] Sincronizando cookies HTTP-only...');
        await syncHttpOnlyCookies();
        console.log('[AUTH_DEBUG][LoginClient] Cookies sincronizados');
        
        // Setar contexto de organização e role
        try {
          console.log('[AUTH_DEBUG][LoginClient] Chamando /api/auth/set-org-context...');
          const contextResponse = await fetch('/api/auth/set-org-context', {
            method: 'POST',
            credentials: 'include',
          });
          
          console.log('[AUTH_DEBUG][LoginClient] set-org-context response status:', contextResponse.status);
          
          if (contextResponse.ok) {
            const contextData = await contextResponse.json();
            console.log('[AUTH_DEBUG][LoginClient] Contexto setado:', contextData);
            // Hard redirect para garantir cookies HTTP-only sejam lidos pelo server/middleware no PWA
            window.location.replace(contextData.redirectTo);
            return;
          } else {
            const errorData = await contextResponse.json().catch(() => ({}));
            console.warn('[AUTH_DEBUG][LoginClient] Falha ao setar contexto:', contextResponse.status, errorData);
          }
        } catch (contextError) {
          console.warn('[AUTH_DEBUG][LoginClient] Erro ao setar contexto:', contextError);
        }
        
        // Fallback caso a API de contexto falhe - também usar hard redirect
        const destination = await getRedirectDestination();
        window.location.replace(destination);
        return;
      }

      // signup
      console.log('[Signup] Iniciando processo de criação de conta para:', cleanEmail);

      // PILAR 1: WhatsApp é obrigatório no signup
      if (!whatsapp.trim()) {
        setWhatsappError('Informe seu WhatsApp para receber avisos importantes sobre a sua conta.');
        setLoading(false);
        return;
      }

      // PILAR 2: Normalizar WhatsApp para formato internacional
      const normalizedWhatsapp = normalizeBrazilWhatsapp(whatsapp);
      if (!normalizedWhatsapp) {
        setWhatsappError('Número de WhatsApp inválido. Use o formato (DDD) 99999-9999');
        setLoading(false);
        return;
      }

      // Salvar o whatsapp normalizado no state para uso posterior
      setNormalizedWhatsappState(normalizedWhatsapp);

      console.log('[Signup] WhatsApp normalizado:', {
        original: whatsapp,
        normalized: normalizedWhatsapp
      });
      
      console.log('[Signup] Dados enviados:', { 
        email: cleanEmail, 
        hasPassword: !!password,
        userData: { name: name || undefined, whatsapp: normalizedWhatsapp }
      });
      
      const { data, error } = await supabase.auth.signUp({
        email: cleanEmail,
        password,
        options: {
          data: { 
            name: name || undefined,
            whatsapp: normalizedWhatsapp
          },
          // Remover emailRedirectTo para usar OTP
        },
      });
      
      console.log('[Signup] Resposta completa do Supabase:');
      console.log('- Data:', data);
      console.log('- Error:', error);
      console.log('- User criado:', !!data?.user);
      console.log('- Session criada:', !!data?.session);
      console.log('- User ID:', data?.user?.id);
      console.log('- Email confirmado:', data?.user?.email_confirmed_at);
      
      if (error) {
        console.error('[Signup] ERRO DETALHADO no signUp:');
        console.error('- Message:', error.message);
        console.error('- Status:', error.status);
        console.error('- Error completo:', error);
        throw error;
      }

      // Capturar lead independente do resultado
      try {
        await fetch('/api/leads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: cleanEmail,
            name: name || null,
            whatsapp: normalizedWhatsapp,
            source: 'signup'
          })
        });
      } catch (leadError) {
        console.error('Erro ao capturar lead:', leadError);
        // Não falhar o processo por erro de lead
      }

      // NOTA: Notificações de boas-vindas (signup_welcome, onboarding_1h, onboarding_d1)
      // serão enfileiradas APÓS a verificação do código, não aqui.
      // Isso garante que apenas usuários verificados recebam as mensagens de onboarding.
      // Ver: src/app/api/auth/confirm-verification/route.ts

      if (!data.session) {
        // fluxo de verificação
        console.log('[Signup] ✅ Usuário criado com sucesso! Iniciando fluxo de verificação');
        console.log('[Signup] 📧 Email para confirmação:', cleanEmail);
        console.log('[Signup] 👤 User ID:', data.user?.id);
        
        // Salvar userId para uso na verificação
        setCurrentUserId(data.user?.id || null);

        // Se usar verificação dual-channel, enviar código customizado
        if (USE_DUAL_CHANNEL_VERIFICATION && data.user?.id) {
          try {
            console.log('[Signup] 🔐 Solicitando código de verificação dual-channel...');
            const verifyResponse = await fetch('/api/auth/request-verification', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                userId: data.user.id,
                email: cleanEmail,
                whatsapp: normalizedWhatsapp,
                name: name || null,
                type: 'signup',
              }),
            });

            const verifyData = await verifyResponse.json();
            
            if (verifyResponse.ok && verifyData.success) {
              console.log('[Signup] ✅ Código de verificação enviado:', verifyData.channels);
              setMode('verify-custom');
              setErr(null);
              return;
            } else {
              console.warn('[Signup] ⚠️ Falha ao enviar código dual-channel, usando OTP padrão');
            }
          } catch (verifyError) {
            console.warn('[Signup] ⚠️ Erro ao solicitar verificação dual-channel:', verifyError);
          }
        }

        // Fallback: Se falhou ao enviar WhatsApp, mostrar erro
        // (OTP por email desativado para evitar duplicação de códigos)
        console.log('[Signup] ❌ Verificação requer WhatsApp cadastrado');
        setErr('Erro ao enviar código de verificação. Certifique-se de ter um WhatsApp válido cadastrado.');
        return;
      }

      // se veio sessão (config pode permitir), sincroniza cookies e redireciona
      console.log('[Signup] ⚡ Sessão criada automaticamente (email confirmation disabled)');
      console.log('[Signup] 🚀 Redirecionando para dashboard...');
      await syncHttpOnlyCookies();
      const destination = await getRedirectDestination();
      router.replace(destination as any);
    } catch (e: any) {
      const message = e?.message || 'Falha na autenticação';
      console.error('[Signup] ❌ ERRO FINAL no processo:');
      console.error('- Message:', message);
      console.error('- Error object:', e);
      console.error('- Stack:', e?.stack);
      setErr(String(message));
    } finally {
      setLoading(false);
    }
  }

  async function onForgotPassword() {
    if (!email.trim()) {
      setErr('Digite seu email primeiro');
      return;
    }
    
    try {
      setLoading(true);
      setSuccessMessage(null);
      setErr(null);
      
      // Usar o novo fluxo de reset com dual-channel
      const response = await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        const respData = data as { channels?: { whatsapp: boolean; email: boolean } };
        const viaEmail = respData?.channels?.email && !respData?.channels?.whatsapp;
        setSuccessMessage(
          viaEmail
            ? 'Se o email existir, você receberá as instruções para redefinir sua senha por email. Verifique sua caixa de entrada e spam.'
            : 'Se o email existir, você receberá as instruções para redefinir sua senha no WhatsApp cadastrado.'
        );
      } else {
        throw new Error(data.error || 'Erro ao solicitar reset');
      }
      
    } catch (e: any) {
      setErr('Erro ao enviar: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-dvh grid place-items-center p-6">
      {mode === 'verify-otp' ? (
        <VerifyOTPComponent
          email={email}
          onBack={() => setMode('signup')}
          onSuccess={async () => {
            await syncHttpOnlyCookies();
            const destination = await getRedirectDestination();
            router.replace(destination as any);
          }}
        />
      ) : mode === 'verify-custom' ? (
        <VerifyCustomCode
          email={email}
          userId={currentUserId || ''}
          type="signup"
          name={name || undefined}
          whatsapp={normalizedWhatsappState || undefined}
          onBack={() => setMode('signup')}
          onSuccess={async () => {
            // Após verificação customizada, tentar obter sessão
            const supabase = getSupabaseBrowser();
            
            // Tentar fazer login automático se possível
            const { data: sessionData } = await supabase.auth.getSession();
            
            if (sessionData?.session) {
              await syncHttpOnlyCookies();
              const destination = await getRedirectDestination();
              router.replace(destination as any);
            } else {
              // Se não tem sessão, redirecionar para login com mensagem de sucesso
              setSuccessMessage('Conta verificada com sucesso! Faça login para continuar.');
              setMode('signin');
            }
          }}
        />
      ) : (
        <div className="w-full max-w-md rounded-2xl border border-gray-200 p-6 shadow-sm">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold">Entrar no DroneOps</h1>
          <p className="text-sm text-gray-500 mt-1">
            {mode === 'signin'
              ? 'Acesse com seu e-mail e senha'
              : 'Crie sua conta para começar'}
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          {mode === 'signup' && (
            <>
              <div className="grid gap-1.5">
                <label className="text-sm font-medium">Nome</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-10 w-full rounded-md border border-gray-300 px-3 outline-none focus:ring-2 focus:ring-black/10"
                  placeholder="Seu nome"
                  autoComplete="name"
                />
              </div>
              
              <div className="grid gap-1.5">
                <label className="text-sm font-medium">WhatsApp <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  value={whatsapp}
                  onChange={(e) => {
                    // Limpar erro quando usuário digita
                    setWhatsappError(null);
                    // Formatação automática do WhatsApp
                    let value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 11) {
                      if (value.length > 6) {
                        value = value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
                      } else if (value.length > 2) {
                        value = value.replace(/(\d{2})(\d+)/, '($1) $2');
                      }
                    }
                    setWhatsapp(value);
                  }}
                  className={`h-10 w-full rounded-md border px-3 outline-none focus:ring-2 focus:ring-black/10 ${
                    whatsappError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="(11) 99999-9999"
                  maxLength={15}
                  required
                />
                {whatsappError ? (
                  <p className="text-xs text-red-600">{whatsappError}</p>
                ) : (
                  <p className="text-xs text-gray-500">
                    Para suporte e atualizações importantes
                  </p>
                )}
              </div>
            </>
          )}

          <div className="grid gap-1.5">
            <label className="text-sm font-medium">E-mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-10 w-full rounded-md border border-gray-300 px-3 outline-none focus:ring-2 focus:ring-black/10"
              placeholder="voce@exemplo.com"
              autoComplete="email"
              required
            />
          </div>

          <div className="grid gap-1.5">
            <label className="text-sm font-medium">Senha</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-10 w-full rounded-md border border-gray-300 px-3 outline-none focus:ring-2 focus:ring-black/10"
              placeholder="••••••••"
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              required
            />
          </div>

          {successMessage && (
            <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700 mb-4">
              {successMessage}
            </div>
          )}

          {err && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="h-10 w-full rounded-md bg-black text-white font-medium disabled:opacity-60"
          >
            {loading
              ? mode === 'signin'
                ? 'Entrando...'
                : 'Criando conta...'
              : mode === 'signin'
                ? 'Entrar'
                : 'Criar conta'}
          </button>
        </form>

        <div className="mt-4 text-center text-sm text-gray-600">
          {mode === 'signin' ? (
            <>
              Ainda não tem conta?{' '}
              <button
                className="underline underline-offset-4"
                onClick={() => setMode('signup')}
                type="button"
              >
                Criar conta
              </button>
              <br />
              <div className="mt-2">
                <button
                  className="underline underline-offset-4 text-blue-600"
                  onClick={onForgotPassword}
                  disabled={loading}
                  type="button"
                >
                  Esqueci minha senha
                </button>
              </div>
            </>
          ) : (
            <>
              Já tem conta?{' '}
              <button
                className="underline underline-offset-4"
                onClick={() => setMode('signin')}
                type="button"
              >
                Fazer login
              </button>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          Ao continuar, você concorda com os Termos e a Política de Privacidade.
        </p>
        </div>
      )}
    </div>
  );
}
